import { Schema } from 'effect';

export const queryOperatorSchema = Schema.Literals([
  '=',
  '!=',
  '>',
  '>=',
  '<',
  '<=',
  '~',
  '!~',
  'IN',
  'NOT IN',
  'IS EMPTY',
  'IS NOT EMPTY',
]);
export type QueryOperator = typeof queryOperatorSchema.Type;

export const equalityOperatorSchema = queryOperatorSchema.pick(['=', '!=']);
export const orderingOperatorSchema = queryOperatorSchema.pick(['=', '!=', '>', '>=', '<', '<=']);
export const textOperatorSchema = queryOperatorSchema.pick(['=', '!=', '~', '!~']);
export const membershipOperatorSchema = queryOperatorSchema.pick(['IN', 'NOT IN']);
export const presenceOperatorSchema = queryOperatorSchema.pick(['IS EMPTY', 'IS NOT EMPTY']);

const fieldDetails = {
  name: Schema.NonEmptyString,
  label: Schema.NonEmptyString,
  description: Schema.optionalKey(Schema.String),
  aliases: Schema.optionalKey(Schema.Array(Schema.NonEmptyString)),
};

const numberValidationSchema = Schema.declare<Schema.Codec<number>>(
  (value): value is Schema.Codec<number> => Schema.isSchema(value),
);

export const idFieldSchema = Schema.Struct({
  ...fieldDetails,
  type: Schema.Literal('id'),
  operators: Schema.optionalKey(
    Schema.NonEmptyArray(
      queryOperatorSchema.pick(['=', '!=', '>', '>=', '<', '<=', 'IN', 'NOT IN']),
    ),
  ),
  valueSchema: Schema.optionalKey(numberValidationSchema),
});
export const numberFieldSchema = Schema.Struct({
  ...fieldDetails,
  type: Schema.Literal('number'),
  operators: Schema.optionalKey(
    Schema.NonEmptyArray(
      queryOperatorSchema.pick([
        '=',
        '!=',
        '>',
        '>=',
        '<',
        '<=',
        'IN',
        'NOT IN',
        'IS EMPTY',
        'IS NOT EMPTY',
      ]),
    ),
  ),
  valueSchema: Schema.optionalKey(numberValidationSchema),
});
export const textFieldSchema = Schema.Struct({
  ...fieldDetails,
  type: Schema.Literal('text'),
  suggestedValues: Schema.optionalKey(Schema.NonEmptyArray(Schema.NonEmptyString)),
  operators: Schema.optionalKey(
    Schema.NonEmptyArray(
      queryOperatorSchema.pick(['=', '!=', '~', '!~', 'IN', 'NOT IN', 'IS EMPTY', 'IS NOT EMPTY']),
    ),
  ),
});
export const choiceFieldSchema = Schema.Struct({
  ...fieldDetails,
  type: Schema.Literal('choice'),
  values: Schema.NonEmptyArray(Schema.NonEmptyString),
  operators: Schema.optionalKey(
    Schema.NonEmptyArray(
      queryOperatorSchema.pick(['=', '!=', 'IN', 'NOT IN', 'IS EMPTY', 'IS NOT EMPTY']),
    ),
  ),
});
export const booleanFieldSchema = Schema.Struct({
  ...fieldDetails,
  type: Schema.Literal('boolean'),
  operators: Schema.optionalKey(Schema.NonEmptyArray(queryOperatorSchema.pick(['=']))),
});
export const dateFieldSchema = Schema.Struct({
  ...fieldDetails,
  type: Schema.Literal('date'),
  operators: Schema.optionalKey(
    Schema.NonEmptyArray(
      queryOperatorSchema.pick(['=', '>', '>=', '<', '<=', 'IS EMPTY', 'IS NOT EMPTY']),
    ),
  ),
});
export const queryFieldSchema = Schema.Union([
  idFieldSchema,
  numberFieldSchema,
  textFieldSchema,
  choiceFieldSchema,
  booleanFieldSchema,
  dateFieldSchema,
]);
export type QueryField = typeof queryFieldSchema.Type;
export type QueryFieldType = QueryField['type'];

const uniqueFieldNames = (fields: readonly QueryField[]): boolean => {
  const names = fields
    .flatMap((field) => [field.name, ...(field.aliases ?? [])])
    .map((name) => name.toLowerCase());
  return new Set(names).size === names.length;
};

export const querySchema = Schema.Struct({
  entity: Schema.NonEmptyString,
  searchPlaceholder: Schema.optionalKey(Schema.NonEmptyString),
  defaultSimpleField: Schema.optionalKey(Schema.NonEmptyString),
  fields: Schema.NonEmptyArray(queryFieldSchema).check(
    Schema.makeFilter(uniqueFieldNames, {
      message: 'Query field names and aliases must be unique',
    }),
  ),
}).check(
  Schema.makeFilter(
    (schema) =>
      schema.defaultSimpleField === undefined ||
      schema.fields.some((field) => field.name === schema.defaultSimpleField),
    { message: 'The default Simple field must be a configured field' },
  ),
);
export type QuerySchema = typeof querySchema.Type;

export const operatorsByType: Readonly<Record<QueryFieldType, readonly QueryOperator[]>> = {
  id: ['=', '!=', 'IN', 'NOT IN', '>', '>=', '<', '<='],
  number: ['=', '!=', '>', '>=', '<', '<=', 'IN', 'NOT IN', 'IS EMPTY', 'IS NOT EMPTY'],
  text: ['~', '=', '!=', '!~', 'IN', 'NOT IN', 'IS EMPTY', 'IS NOT EMPTY'],
  choice: ['=', '!=', 'IN', 'NOT IN', 'IS EMPTY', 'IS NOT EMPTY'],
  boolean: ['='],
  date: ['>=', '<=', '>', '<', '=', 'IS EMPTY', 'IS NOT EMPTY'],
};

export const operatorHelp: Readonly<Record<QueryOperator, string>> = {
  '=': 'equals',
  '!=': 'does not equal',
  '>': 'greater than',
  '>=': 'greater than or equal',
  '<': 'less than',
  '<=': 'less than or equal',
  '~': 'contains',
  '!~': 'does not contain',
  IN: 'is one of (a, b, c)',
  'NOT IN': 'is none of (a, b, c)',
  'IS EMPTY': 'has no value',
  'IS NOT EMPTY': 'has a value',
};

export const dateShortcuts: readonly { readonly value: string; readonly help: string }[] = [
  { value: 'today', help: 'since midnight' },
  { value: 'yesterday', help: 'the previous calendar day' },
  { value: '-7d', help: '7 days ago' },
  { value: '-30d', help: '30 days ago' },
  { value: '"last 7 days"', help: 'rolling window' },
  { value: '"this month"', help: 'calendar month to date' },
  { value: '"last month"', help: 'previous calendar month' },
  { value: '2026-01-31', help: 'an exact date' },
];

export const findField = (schema: QuerySchema, name: string): QueryField | undefined => {
  const wanted = name.toLowerCase();
  return schema.fields.find(
    (field) =>
      field.name.toLowerCase() === wanted ||
      (field.aliases ?? []).some((alias) => alias.toLowerCase() === wanted),
  );
};

export const operatorsFor = (field: QueryField): readonly QueryOperator[] =>
  field.operators ?? operatorsByType[field.type];

export const fieldsMatching = (schema: QuerySchema, prefix: string): readonly QueryField[] => {
  const wanted = prefix.toLowerCase();
  return schema.fields.filter(
    (field) =>
      field.name.toLowerCase().startsWith(wanted) ||
      (field.aliases ?? []).some((alias) => alias.toLowerCase().startsWith(wanted)),
  );
};
